import { ReactNode, Fragment, FC, useEffect } from 'react';
import { createFragmentContainer, graphql, RelayProp } from 'react-relay/legacy';
import { connect } from 'react-redux';
import { getAvailableBankAccountPaymentMethods } from '../../helpers/checkoutPayment';
import {
    changeBankAccountSection,
    selectBankAccountWireOrCheck,
} from '../../actions/checkoutPaymentActions';
import CheckoutRadioContainer from '../../shared/CheckoutRadioContainer';
import { defineMessages, FormattedMessage, useIntl } from 'dibs-react-intl';
import RowContainer from '../../shared/CheckoutRowContainer';
import { PANES } from '../../constants/checkoutConstants';

// components
import ArrowLeft from 'dibs-icons/exports/legacy/ArrowLeft';
import CheckoutBankAccountListWrapper from './CheckoutBankAccountListWrapper';
import CheckoutACHForm from '../CheckoutACHForm';
import CheckoutACHFormStripe from '../stripe/CheckoutACHFormStripe';
import CheckoutWireTransferForm from '../wireTransfer/CheckoutWireTransferForm';
import { Link } from 'dibs-elements/exports/Link';

import { isAddingBankAccount } from '../../reducers/checkoutPaymentReducer';
import { addBankAccount, bankAccountFormCanceled } from '../../actions/checkoutPaymentActions';
import { buyerHasBankAccounts } from '../../helpers/checkoutPayment';
import { CheckoutWireTransferInformation } from './CheckoutWireTransferInformation';

import styles from './styles/checkoutBankAccounts.scss';

import { CheckoutBankAccounts_viewer$data } from './__generated__/CheckoutBankAccounts_viewer.graphql';
import { CheckoutBankAccounts_buyer$data } from './__generated__/CheckoutBankAccounts_buyer.graphql';
import { CheckoutBankAccounts_transaction$data } from './__generated__/CheckoutBankAccounts_transaction.graphql';

type Props = {
    viewer: CheckoutBankAccounts_viewer$data;
    buyer: CheckoutBankAccounts_buyer$data;
    transaction: CheckoutBankAccounts_transaction$data;
    wrapperClass: string;
    onChangeBankAccountSection: (arg0: string) => void;
    onSelectBankAccountWireOrCheck: (arg0: string) => void;
    bankAccountMethodSelected: string;
    bankAccountSectionSelected: string;
    isAddingAccount: boolean;
    onAddBankAccount: () => void;
    onBankAccountFormCanceled: () => void;
    onBankAccountMethodSelect: () => void;
    expandableBankAccountList?: boolean;
    checkoutPaymentErrors: ObjectType;
    onValidationUpdate: () => void;
    onValidationError: () => void;
    relay?: RelayProp;
};

type ShowMap = {
    showWireTransfer: boolean;
    showCheck: boolean;
    showACH: boolean;
};

type ShowMapKey = keyof ShowMap;

const messages = defineMessages({
    wireTransfer: {
        id: 'checkoutBankAccounts.wireTransfer',
        defaultMessage: 'Wire Transfer',
    },
    check: {
        id: 'checkoutBankAccounts.check',
        defaultMessage: 'Check',
    },
});

const CheckoutBankAccounts: FC<Props> = props => {
    const intl = useIntl();
    const {
        viewer,
        wrapperClass,
        transaction,
        onChangeBankAccountSection,
        onSelectBankAccountWireOrCheck,
        bankAccountMethodSelected,
        bankAccountSectionSelected,
        buyer,
        isAddingAccount,
        onAddBankAccount,
        onBankAccountFormCanceled,
        onBankAccountMethodSelect,
        expandableBankAccountList,
        checkoutPaymentErrors,
        onValidationUpdate,
        onValidationError,
    } = props;

    const buyerHasAccounts = buyerHasBankAccounts({ buyer });

    const showMap = getAvailableBankAccountPaymentMethods(transaction);

    const showArray = Object.keys(showMap).filter(toShow => showMap[toShow as ShowMapKey]);
    const ACHenabled = showArray.some(val => val === 'showACH');
    const checkEnabled = showArray.some(val => val === 'showCheck');
    const wireEnabled = showArray.some(val => val === 'showWireTransfer');
    const checkIsExcluded = ACHenabled && !checkEnabled && wireEnabled;

    const initialSelected = ACHenabled ? 'all' : 'wireAndCheck';

    const sharedProps = {
        viewer,
        buyer,
        checkoutPaymentErrors: {},
        showArray,
        isMobile: false,
    };

    const wireAndCheckProps = {
        wireProps: {
            label: intl.formatMessage(messages.wireTransfer),
            type: PANES.PANE_PAYMENT,
            serviceId: 'wireTransfer',
            noMargin: true,
            isSelected: bankAccountMethodSelected === 'wireTransfer',
            onSelect: (value: string) => onSelectBankAccountWireOrCheck(value),
            wrapperClass: styles.wireAndCheckRadios,
        },
        checkProps: {
            label: intl.formatMessage(messages.check),
            type: PANES.PANE_PAYMENT,
            serviceId: 'check',
            noMargin: true,
            isSelected: bankAccountMethodSelected === 'check',
            onSelect: (value: string) => onSelectBankAccountWireOrCheck(value),
            wrapperClass: styles.wireAndCheckRadios,
        },
    };

    const sectionIndicatorToUse = bankAccountSectionSelected || initialSelected;
    const showAll =
        (sharedProps.showArray.length === 3 || checkIsExcluded) && sectionIndicatorToUse === 'all';
    const showWireAndCheck =
        sharedProps.showArray.length > 1 && sectionIndicatorToUse === 'wireAndCheck';
    const showWire = wireEnabled && !ACHenabled && !checkEnabled;
    const showACH = ACHenabled && !showWireAndCheck;

    useEffect(() => {
        if ((showWire || showWireAndCheck) && bankAccountMethodSelected === 'bankAccount') {
            onSelectBankAccountWireOrCheck('wireTransfer');
        }
    }, [showWire, showWireAndCheck, bankAccountMethodSelected, onSelectBankAccountWireOrCheck]);

    const bankAccountListProps = {
        buyer,
        viewer,
        transaction,
        expandableBankAccountList,
        checkoutPaymentErrors,
        onValidationUpdate,
        onValidationError,
    };

    const { wireProps, checkProps } = wireAndCheckProps;
    const addCancelAction = isAddingAccount ? onBankAccountFormCanceled : onAddBankAccount;
    const addCancelText = isAddingAccount ? 'Cancel' : 'Add New Account';

    const isStripeACHEnabled = viewer?.stripeIsEnabled && transaction?.isStripeAchEligible;

    return (
        <div className={wrapperClass}>
            {showAll && (
                <Fragment>
                    <p className={styles.information}>
                        <FormattedMessage
                            id="checkout.CheckoutBankAccounts.all.line1"
                            defaultMessage="Pay securely through your US bank account."
                        />
                    </p>
                    <p className={styles.wireOrCheckButton}>
                        <FormattedMessage
                            id="checkout.CheckoutBankAccounts.all.wireTransferOrCheck"
                            defaultMessage="You may also send a {checkIsExcluded, select,
                                true {<a>Wire Transfer</a>}
                                other {<a>Wire Transfer or mail a Check</a>}
                            }."
                            values={{
                                checkIsExcluded,
                                a: (content: ReactNode) => (
                                    <Link
                                        className={styles.wireAndCheckButton}
                                        onClick={() => onChangeBankAccountSection('wireAndCheck')}
                                        id="wireAndCheck"
                                        dataTn="wireAndCheck"
                                    >
                                        {content}
                                    </Link>
                                ),
                            }}
                        />
                    </p>
                </Fragment>
            )}
            {showACH && buyerHasAccounts && (
                <Fragment>
                    <CheckoutBankAccountListWrapper
                        {...bankAccountListProps}
                        onBankAccountMethodSelect={onBankAccountMethodSelect}
                    />
                    <Link
                        className={styles.addAccountButton}
                        onClick={addCancelAction}
                        dataTn={`${isAddingAccount ? 'cancel-' : ''}add-new-account`}
                    >
                        {addCancelText}
                    </Link>
                </Fragment>
            )}
            {showACH &&
                (isAddingAccount || !buyerHasAccounts) &&
                (isStripeACHEnabled ? (
                    <CheckoutACHFormStripe
                        transaction={transaction}
                        buyer={buyer}
                        viewer={viewer}
                    />
                ) : (
                    <CheckoutACHForm {...sharedProps} />
                ))}
            {showWireAndCheck && (
                <RowContainer
                    wrapperClass={styles.rowContainerWrapper}
                    noMargin
                    isClickable={false}
                    noHorizontalPadding={sharedProps.isMobile}
                >
                    {ACHenabled && (
                        <p className={styles.information}>
                            <Link
                                className={styles.backButton}
                                onClick={() => onChangeBankAccountSection('all')}
                                id="all"
                                dataTn="all"
                            >
                                <FormattedMessage
                                    id="checkoutBankAccounts.goBack"
                                    defaultMessage="{arrowIcon} Back"
                                    values={{
                                        arrowIcon: <ArrowLeft className={styles.backArrow} />,
                                    }}
                                />
                            </Link>
                        </p>
                    )}
                    <div className={styles.title}>
                        <FormattedMessage
                            id="checkout.CheckoutBankAccounts.wire.title"
                            defaultMessage="Wire Transfer or Check"
                        />
                    </div>
                    <CheckoutWireTransferInformation checkEnabled={checkEnabled} />
                    {checkEnabled && (
                        <div className={styles.wireAndCheckRadiosContainer}>
                            <CheckoutRadioContainer {...wireProps} />
                            <CheckoutRadioContainer {...checkProps} />
                        </div>
                    )}
                </RowContainer>
            )}
            {showWire && !showWireAndCheck && (
                <RowContainer
                    wrapperClass={styles.rowContainerWrapper}
                    noMargin
                    isClickable={false}
                    noHorizontalPadding={sharedProps.isMobile}
                >
                    <div className={styles.title}>
                        <FormattedMessage
                            id="checkout.CheckoutBankAccounts.wireOnly.title"
                            defaultMessage="Wire Transfer"
                        />
                    </div>
                    <CheckoutWireTransferInformation />
                </RowContainer>
            )}
            {(showWireAndCheck || showWire) && bankAccountMethodSelected === 'wireTransfer' && (
                <CheckoutWireTransferForm viewer={viewer} buyer={buyer} relay={props.relay} />
            )}
        </div>
    );
};

type ConnectPaymentType = {
    bankAccountMethodSelected: string;
    bankAccountSectionSelected: string;
    isAddingAccount: () => void;
};

type ConnectType = {
    payment: ConnectPaymentType;
};

const ConnectedCheckoutBankAccounts = connect(
    ({ payment }: ConnectType) => {
        return {
            bankAccountMethodSelected: payment.bankAccountMethodSelected,
            bankAccountSectionSelected: payment.bankAccountSectionSelected,
            isAddingAccount: isAddingBankAccount(payment),
        };
    },
    {
        onChangeBankAccountSection: changeBankAccountSection,
        onSelectBankAccountWireOrCheck: selectBankAccountWireOrCheck,
        onAddBankAccount: addBankAccount,
        onBankAccountFormCanceled: bankAccountFormCanceled,
    }
)(CheckoutBankAccounts);

export default createFragmentContainer(ConnectedCheckoutBankAccounts, {
    viewer: graphql`
        fragment CheckoutBankAccounts_viewer on Viewer {
            ...checkoutPayment_viewer
            ...CheckoutPaymentError_viewer
            ...CheckoutACHForm_viewer
            ...CheckoutBankAccountListWrapper_viewer
            ...CheckoutWireTransferForm_viewer
            ...CheckoutACHFormStripe_viewer

            stripeIsEnabled: featureFlag(feature: "stripeCheckout")
        }
    `,
    buyer: graphql`
        fragment CheckoutBankAccounts_buyer on User {
            ...checkoutPayment_buyer
            ...CheckoutACHForm_buyer
            ...CheckoutBankAccountListWrapper_buyer
            ...CheckoutWireTransferForm_buyer
            ...CheckoutACHFormStripe_buyer
        }
    `,
    transaction: graphql`
        fragment CheckoutBankAccounts_transaction on Transaction {
            isStripeAchEligible: isPaymentMethodEligible(
                paymentMethod: ACH
                paymentProcessor: STRIPE
            )
            ...CheckoutBankAccountListWrapper_transaction
            ...checkoutPayment_transaction
            ...CheckoutACHFormStripe_transaction
        }
    `,
});
